import { mergeDeepPure } from 'client/utils';
import { CSSProperties } from 'styled-components';
import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { context } from '@shared-services/context-service';
import normalizeStylesToCamelCase from './normalizeStylesToCamelCase';

type ModeledStruct<CSS> = {
    [key in RuntimeMediaQuery]?: CSS;
};

interface CSSNested {
    [selector: string]: CSSProperties;
}

export type ResponsiveStylesNested = ModeledStruct<CSSNested>;
export type ResponsiveStylesWide = ModeledStruct<
    CSSProperties | CSSNested | (CSSProperties & CSSNested)
>;

export type ResponsiveStylesProp = DeepArray<
    ResponsiveStylesWide | undefined | false
>;

function mergeStyleOntoBase(
    baseStyles: ResponsiveStylesWide,
    newStyles?: ResponsiveStylesWide
) {
    if (!newStyles) {
        return baseStyles;
    }
    const _newStyles = normalizeStylesToCamelCase(newStyles);
    return mergeDeepPure(baseStyles, _newStyles);
}

export function mergeResponsiveStyles(
    styles: ResponsiveStylesProp
): ResponsiveStylesWide {
    const arrayedStyles: ResponsiveStylesProp[] = Array.isArray(styles)
        ? styles
        : [styles];
    const flatStyles = (arrayedStyles.flat as any)(Infinity) as Array<
        ResponsiveStylesWide | undefined
    >;

    return flatStyles.reduce(mergeStyleOntoBase, {} as ResponsiveStylesWide);
}

export function nestResponsiveStyles(
    styleModel: ResponsiveStylesStructured = {},
    nestUnder: {
        pseudoSelector?: 'hover' | 'focus' | 'focus-visible';
        innerSelector?: string;
    }
): ResponsiveStylesNested {
    const { pseudoSelector, innerSelector } = nestUnder;

    const newStylesObject: ResponsiveStylesNested = {};
    (
        Object.entries(styleModel) as Array<[RuntimeMediaQuery, CSSProperties]>
    ).forEach(([mQ, cssProps]) => {
        /**
         * TODO @todo clean or extract function for keys
         */
        let cssObj: CSSNested;
        if (pseudoSelector) {
            cssObj = {
                [`&:${pseudoSelector} ${innerSelector ? innerSelector : ''}`]:
                    cssProps,
            };
            if (context.isEditor) {
                cssObj[
                    `&.${pseudoSelector} ${innerSelector ? innerSelector : ''}`
                ] = cssProps;
            }
        } else if (innerSelector) {
            cssObj = {
                [innerSelector]: cssProps,
            };
        } else {
            cssObj = {};
        }
        newStylesObject[mQ] = cssObj;
    });
    return newStylesObject;
}

type DeepArray<T> = T | Array<T | DeepArray<T>>;

export function pickResponsiveStyles(
    styles: ResponsiveStylesStructured,
    properties: Set<string>
): ResponsiveStylesStructured {
    const result: ResponsiveStylesStructured = {};

    Object.entries(styles).forEach(([key, value]) => {
        result[key as RuntimeMediaQuery] = Object.entries(value).reduce(
            (acc: Record<string, any>, [prop, val]) => {
                if (properties.has(prop)) {
                    acc[prop] = val;
                }
                return acc;
            },
            {}
        );
    });

    return result;
}
